import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Modal,
  Badge,
  Alert,
  Descriptions,
  Dropdown,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  FilePdfOutlined,
  EyeOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  FileTextOutlined,
  CloudUploadOutlined,
  SafetyCertificateOutlined,
  FilterOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  canSendInvoiceToKSeF,
  isKSeFSurfaceVisible,
} from '../modules/invoices/invoiceAccessPolicy';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import dayjs from 'dayjs';
import ListIdentity from '../components/layout/ListIdentity';
import '../components/layout/AppPage.css';
import './Invoices.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Paragraph, Text } = Typography;

interface Invoice {
  id: string;
  number: string | null;
  sourceSnapshot?: unknown;
  orderId: string;
  orderNumber?: string;
  /** Canonical operator-facing OMS number (manual number when active, raw otherwise). */
  displayOrderNumber?: string;
  customerName?: string;
  type: 'FINAL' | 'PROFORMA' | 'ADVANCE';
  status: 'DRAFT' | 'APPROVED' | 'ISSUED' | 'PAID' | 'CANCELLED';
  issueDate: string;
  dueDate?: string;
  totalAmount: number | null;
  currency: string;
  pdfUrl?: string;
  ksefNumber?: string;
  ksefStatus?: 'NONE' | 'PENDING' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'ERROR';
  ksefSentAt?: string;
  ksefReferenceNumber?: string;
  createdAt: string;
  updatedAt: string;
}

interface KSeFStatus {
  /** True when the KSeF module + integration are on (gate for the whole surface). */
  moduleEnabled?: boolean;
  /** True when the external KSeF system is reachable/ok. */
  available: boolean;
  mode: string;
  message: string;
}

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['invoices', 'common']);
  const feedback = useFeedback();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingToKSeF, setSendingToKSeF] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [ksefStatus, setKsefStatus] = useState<KSeFStatus | null>(null);
  const [xmlPreviewVisible, setXmlPreviewVisible] = useState(false);
  const [xmlContent, setXmlContent] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const currentUser = authService.getCurrentUser();
  const canSendToKSeF = canSendInvoiceToKSeF(currentUser);

  // Master gate for the whole KSeF surface. Driven by status.moduleEnabled: the
  // surface is present when the KSeF module + integration are on. When the
  // module is off we hide every KSeF affordance so the page looks like KSeF does
  // not exist. (isKSeFSurfaceVisible falls back to available===true for a stale
  // backend that predates the moduleEnabled/available split.)
  const ksefSurfaceVisible = isKSeFSurfaceVisible(ksefStatus);

  // Module on but the external KSeF system is temporarily unreachable. The
  // surface stays visible (operators keep seeing their sent-invoice statuses),
  // but we show an offline Tag + warning Alert. moduleEnabled may be absent on a
  // stale backend; in that case the surface is gated on available===true so this
  // is never true, matching the old behavior.
  const ksefOffline = ksefSurfaceVisible && ksefStatus?.available === false;

  // Filter states
  const [filters, setFilters] = useState<{
    status: string[];
    type: string[];
    ksefStatus: string[];
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    search: string;
  }>({
    status: [],
    type: [],
    ksefStatus: [],
    dateRange: null,
    search: ''
  });

  useEffect(() => {
    fetchInvoices();
    checkKSeFStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/invoices');
      setInvoices(response.data);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'invoices:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const checkKSeFStatus = async () => {
    try {
      const response = await api.get('/invoices/ksef/status');
      setKsefStatus(response.data);
      // The probe message may be an English backend string; keep it out of the
      // Polish-first UI and only surface it for diagnostics.
      if (response.data?.available === false && response.data?.message) {
        console.debug('KSeF status probe:', response.data.message);
      }
    } catch (error) {
      console.error('Failed to check KSeF status:', error);
    }
  };

  const handleDownloadPDF = async (id: string, number: string) => {
    setDownloading(id);
    try {
      const response = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${number.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('invoices:success.pdfDownloaded', { number }));
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'invoices:error.pdfFailed', undefined, { number }),
      );
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewXML = async (id: string, number: string) => {
    try {
      const response = await api.get(`/invoices/${id}/ksef/xml`);
      setXmlContent(response.data);
      setXmlPreviewVisible(true);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'invoices:error.xmlFailed', undefined, { number }),
      );
    }
  };

  const handleSendToKSeF = async (id: string, number: string) => {
    setSendingToKSeF(id);
    try {
      await api.post(`/invoices/${id}/ksef/send`);
      feedback.success(t('invoices:success.sentToKSeF', { number }));
      fetchInvoices();
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'invoices:error.sendToKSeFailed', undefined, { number }),
      );
    } finally {
      setSendingToKSeF(null);
    }
  };

  const handleValidateKSeF = async (id: string, number: string) => {
    try {
      const response = await api.get(`/invoices/${id}/ksef/validate`);
      const validation = response.data;

      if (validation.valid) {
        feedback.success(t('invoices:validation.valid', { number }));
      } else {
        Modal.warning({
          title: t('invoices:validation.invalid'),
          content: (
            <div>
              {validation.errors.map((err: string, i: number) => (
                <Paragraph key={i} type="danger">{err}</Paragraph>
              ))}
              {validation.warnings.map((warn: string, i: number) => (
                <Paragraph key={i} type="warning">{warn}</Paragraph>
              ))}
            </div>
          )
        });
      }
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'invoices:error.validationFailed', undefined, { number }),
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'default';
      case 'ISSUED': return 'processing';
      case 'PAID': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'FINAL': return 'blue';
      case 'PROFORMA': return 'orange';
      case 'ADVANCE': return 'purple';
      default: return 'default';
    }
  };

  const getKSeFStatusIcon = (status?: string) => {
    switch (status) {
      case 'ACCEPTED': return <CheckCircleOutlined />;
      case 'SENT':
      case 'PENDING': return <SyncOutlined spin />;
      case 'REJECTED':
      case 'ERROR': return <CloseCircleOutlined />;
      default: return undefined;
    }
  };

  const getKSeFStatusColor = (status?: string) => {
    switch (status) {
      case 'ACCEPTED': return 'success';
      case 'SENT': return 'processing';
      case 'PENDING': return 'warning';
      case 'REJECTED':
      case 'ERROR': return 'error';
      default: return 'default';
    }
  };

  // Filter documents
  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];

    if (filters.status.length > 0) {
      filtered = filtered.filter(inv => filters.status.includes(inv.status));
    }

    if (filters.type.length > 0) {
      filtered = filtered.filter(inv => filters.type.includes(inv.type));
    }

    if (filters.ksefStatus.length > 0) {
      filtered = filtered.filter(inv =>
        filters.ksefStatus.includes(inv.ksefStatus || 'NONE')
      );
    }

    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day');
      const endDate = filters.dateRange[1].endOf('day');
      filtered = filtered.filter(inv => {
        const issueDate = dayjs(inv.issueDate);
        return issueDate.isAfter(startDate) && issueDate.isBefore(endDate);
      });
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(inv =>
        (inv.number || '').toLowerCase().includes(searchLower) ||
        inv.customerName?.toLowerCase().includes(searchLower) ||
        inv.orderNumber?.toLowerCase().includes(searchLower) ||
        inv.displayOrderNumber?.toLowerCase().includes(searchLower) ||
        inv.ksefNumber?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [invoices, filters]);

  const clearFilters = () => {
    setFilters({
      status: [],
      type: [],
      ksefStatus: [],
      dateRange: null,
      search: ''
    });
  };

  const draftLabel = t('invoices:workflow.page.status.draft');
  const openInvoice = (record: Invoice) => {
    if (record.sourceSnapshot && authService.canManageInvoiceImports()) {
      navigate(`/app/sales-invoices/${record.id}`);
      return;
    }
    setPreviewInvoice(record);
    setPreviewVisible(true);
  };

  const getActionMenuItems = (record: Invoice): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [
      { key: 'view', icon: <EyeOutlined />, label: t('common:view') },
      { key: 'pdf', icon: <FilePdfOutlined />, label: t('invoices:actions.downloadPdf') },
    ];
    if (ksefSurfaceVisible && record.type !== 'PROFORMA') {
      items.push(
        { key: 'xml', icon: <FileTextOutlined />, label: t('invoices:actions.previewXml') },
        { key: 'validate', icon: <SafetyCertificateOutlined />, label: t('invoices:actions.validateKSeF') },
      );
      if (canSendToKSeF && record.status === 'ISSUED' && (!record.ksefStatus || record.ksefStatus === 'NONE' || record.ksefStatus === 'ERROR')) {
        items.push({ key: 'send', icon: <CloudUploadOutlined />, label: t('invoices:actions.sendToKSeF') });
      }
    }
    return items;
  };

  const handleAction = (key: string, record: Invoice) => {
    const number = record.number || draftLabel;
    if (key === 'view') openInvoice(record);
    if (key === 'pdf') void handleDownloadPDF(record.id, number);
    if (key === 'xml') void handlePreviewXML(record.id, number);
    if (key === 'validate') void handleValidateKSeF(record.id, number);
    if (key === 'send') void handleSendToKSeF(record.id, number);
  };

  const renderKSeFTag = (record: Invoice) => {
    if (record.type === 'PROFORMA') return <Tag>{t('invoices:ksef.notApplicable')}</Tag>;
    const status = record.ksefStatus || 'NONE';
    return (
      <Tag color={getKSeFStatusColor(status)} icon={getKSeFStatusIcon(status)}>
        {t(`invoices:ksef.status.${status.toLowerCase()}`)}
      </Tag>
    );
  };

  const columns = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center' as const,
      fixed: 'left' as const,
      render: (record: Invoice) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getActionMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              handleAction(key, record);
            },
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            aria-label={t('common:actions')}
            loading={downloading === record.id || sendingToKSeF === record.id}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      ),
    },
    {
      title: t('invoices:fields.number'),
      dataIndex: 'number',
      key: 'number',
      width: 262,
      sorter: (a: Invoice, b: Invoice) => (a.number || '').localeCompare(b.number || ''),
      render: (number: string, record: Invoice) => (
        <ListIdentity
          name={number || draftLabel}
          onOpen={() => openInvoice(record)}
          tags={(
            <>
              <Tag color={getStatusColor(record.status)}>
                {t(`invoices:status.${record.status.toLowerCase()}`)}
              </Tag>
              {ksefSurfaceVisible && renderKSeFTag(record)}
            </>
          )}
          // The invoice belongs to one ZK: its number sits on the meta line, as on „Dokumenty”.
          meta={[
            t(`invoices:types.${record.type.toLowerCase()}`),
            record.displayOrderNumber || record.orderNumber ? (
              <button
                type="button"
                className="app-page__cell-link"
                onClick={() => navigate(`/app/orders/${record.orderId}`)}
              >
                {t('invoices:list.order', { number: record.displayOrderNumber || record.orderNumber })}
              </button>
            ) : null,
            ksefSurfaceVisible && record.ksefNumber ? `${t('invoices:ksef.number')}: ${record.ksefNumber}` : null,
          ]}
        />
      ),
    },
    {
      title: t('invoices:fields.issueDate'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 172,
      render: (date: string) => <span className="app-page__num">{formatDateEuropean(date)}</span>,
      sorter: (a: Invoice, b: Invoice) => dayjs(a.issueDate).unix() - dayjs(b.issueDate).unix(),
    },
    {
      title: t('invoices:fields.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 204,
      render: (name: string) => <span className="app-page__party" title={name || undefined}>{name || '-'}</span>,
    },
    {
      title: t('invoices:fields.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 160,
      render: (date: string) => (date
        ? <span className="app-page__num">{formatDateEuropean(date)}</span>
        : <span className="app-page__muted">-</span>),
    },
    {
      title: t('invoices:fields.amount'),
      key: 'amount',
      width: 130,
      align: 'right' as const,
      render: (record: Invoice) => (record.totalAmount == null
        ? <span className="app-page__muted">—</span>
        : <span className="app-page__num">{formatCurrency(record.totalAmount, record.currency)}</span>),
      sorter: (a: Invoice, b: Invoice) => (a.totalAmount ?? -Infinity) - (b.totalAmount ?? -Infinity),
    },
  ];
  const tableMinWidth = columns.reduce((sum, column) => sum + (typeof column.width === 'number' ? column.width : 0), 0);
  const advancedFilterCount = [
    filters.type.length > 0,
    filters.status.length > 0,
    filters.ksefStatus.length > 0,
    Boolean(filters.dateRange && filters.dateRange[0] && filters.dateRange[1]),
  ].filter(Boolean).length;
  const hasAnyFilter = advancedFilterCount > 0 || Boolean(filters.search.trim());

  return (
    <div className="app-page app-page--modern invoices-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('invoices:filters.search')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            prefix={<SearchOutlined />}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('invoices:filters.toggle')}
              </Button>
            </Badge>
            {hasAnyFilter && (
              <Button type="link" onClick={clearFilters}>
                {t('common:clearFilters')}
              </Button>
            )}
            {ksefSurfaceVisible && (
              <Tag color={ksefOffline ? 'error' : 'success'}>
                KSeF: {ksefOffline ? t('invoices:ksef.offline') : t('invoices:ksef.online')}
              </Tag>
            )}
            <span className="app-page__count" aria-label={t('common:totalItems', { count: filteredInvoices.length })}>
              {filteredInvoices.length}
            </span>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                mode="multiple"
                aria-label={t('invoices:filters.type')}
                placeholder={t('invoices:filters.type')}
                value={filters.type}
                onChange={(value) => setFilters({ ...filters, type: value })}
                allowClear
              >
                <Option value="FINAL">{t('invoices:types.final')}</Option>
                <Option value="PROFORMA">{t('invoices:types.proforma')}</Option>
                <Option value="ADVANCE">{t('invoices:types.advance')}</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                mode="multiple"
                aria-label={t('invoices:filters.status')}
                placeholder={t('invoices:filters.status')}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
                allowClear
              >
                <Option value="DRAFT">{t('invoices:status.draft')}</Option>
                <Option value="ISSUED">{t('invoices:status.issued')}</Option>
                <Option value="PAID">{t('invoices:status.paid')}</Option>
                <Option value="CANCELLED">{t('invoices:status.cancelled')}</Option>
              </Select>
            </Col>
            {ksefSurfaceVisible && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  mode="multiple"
                  aria-label={t('invoices:filters.ksefStatus')}
                  placeholder={t('invoices:filters.ksefStatus')}
                  value={filters.ksefStatus}
                  onChange={(value) => setFilters({ ...filters, ksefStatus: value })}
                  allowClear
                >
                  <Option value="NONE">{t('invoices:ksef.status.none')}</Option>
                  <Option value="PENDING">{t('invoices:ksef.status.pending')}</Option>
                  <Option value="SENT">{t('invoices:ksef.status.sent')}</Option>
                  <Option value="ACCEPTED">{t('invoices:ksef.status.accepted')}</Option>
                  <Option value="ERROR">{t('invoices:ksef.status.error')}</Option>
                </Select>
              </Col>
            )}
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                format="DD.MM.YYYY"
                placeholder={[t('invoices:filters.dateFrom'), t('invoices:filters.dateTo')]}
              />
            </Col>
          </Row>
        )}

        {/* Module on but the KSeF servers are unreachable: keep the surface but warn.
            Operator-facing copy stays the i18n string; the raw backend probe
            message (often English) goes to the console for diagnostics only. */}
        {ksefOffline && (
          <Alert className="app-page__notice" message={t('invoices:ksef.systemUnavailable')} type="warning" showIcon />
        )}

        <Table
          className="invoices-table app-page__list-table"
          columns={columns}
          dataSource={filteredInvoices}
          rowKey="id"
          loading={loading}
          tableLayout="fixed"
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('common:totalItems', { count: total })
          }}
          scroll={{ x: tableMinWidth }}
        />
      </section>

      {/* Invoice Preview Modal */}
      <Modal
        title={t('invoices:preview.title')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            {t('common:close')}
          </Button>,
          <Button
            key="pdf"
            type="primary"
            icon={<FilePdfOutlined />}
            onClick={() => previewInvoice && handleDownloadPDF(previewInvoice.id, previewInvoice.number || t('invoices:workflow.page.status.draft'))}
            loading={downloading === previewInvoice?.id}
          >
            {t('invoices:actions.downloadPdf')}
          </Button>
        ]}
        width={700}
      >
        {previewInvoice && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('invoices:fields.number')} span={2}>
              <Text strong>{previewInvoice.number}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.type')}>
              <Tag color={getTypeColor(previewInvoice.type)}>
                {t(`invoices:types.${previewInvoice.type.toLowerCase()}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.status')}>
              <Tag color={getStatusColor(previewInvoice.status)}>
                {t(`invoices:status.${previewInvoice.status.toLowerCase()}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.issueDate')}>
              {formatDateEuropean(previewInvoice.issueDate)}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.dueDate')}>
              {previewInvoice.dueDate ? formatDateEuropean(previewInvoice.dueDate) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.customer')} span={2}>
              {previewInvoice.customerName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.orderNumber')}>
              {previewInvoice.displayOrderNumber || previewInvoice.orderNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('invoices:fields.amount')}>
              <Text strong className="app-page__num">{previewInvoice.totalAmount == null ? '—' : formatCurrency(previewInvoice.totalAmount, previewInvoice.currency)}</Text>
            </Descriptions.Item>

            {ksefSurfaceVisible && previewInvoice.type !== 'PROFORMA' && (
              <>
                <Descriptions.Item label={t('invoices:ksef.status.label')} span={2}>
                  <Tag color={getKSeFStatusColor(previewInvoice.ksefStatus)} icon={getKSeFStatusIcon(previewInvoice.ksefStatus)}>
                    {t(`invoices:ksef.status.${(previewInvoice.ksefStatus || 'none').toLowerCase()}`)}
                  </Tag>
                </Descriptions.Item>
                {previewInvoice.ksefNumber && (
                  <Descriptions.Item label={t('invoices:ksef.number')} span={2}>
                    {previewInvoice.ksefNumber}
                  </Descriptions.Item>
                )}
                {previewInvoice.ksefSentAt && (
                  <Descriptions.Item label={t('invoices:ksef.sentAt')} span={2}>
                    {formatDateEuropean(previewInvoice.ksefSentAt)}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* XML Preview Modal */}
      <Modal
        title={t('invoices:preview.xmlTitle')}
        open={xmlPreviewVisible}
        onCancel={() => setXmlPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setXmlPreviewVisible(false)}>
            {t('common:close')}
          </Button>
        ]}
        width={800}
      >
        <pre className="invoices-xml-preview">
          {xmlContent}
        </pre>
      </Modal>
    </div>
  );
};

export default Invoices;
